import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Space, Skeleton, Modal } from "antd";
import Container from "../../../../components/paper/container";
import useProperty from "../../../../hooks/property/useProperty";
import Header from "./Header";
import Gallery from "./Gallery";
import Subline from "./Subline";
import BadgeRow from "./BadgeRow";
import BodyGrid from "./BodyGrid";

const parseAmenities = (raw) =>
  Array.isArray(raw)
    ? raw
    : typeof raw === "string"
    ? raw
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean)
    : [];

export default function PropertyDetailPage() {
  const { ownerId, id } = useParams();
  const { data: property, loading, error } = useProperty(ownerId, id);
  const [showAll, setShowAll] = useState(false);

  const images = useMemo(
    () => property?.images || property?.photos || [],
    [property]
  );
  const amenities = useMemo(
    () => parseAmenities(property?.amenities),
    [property]
  );

  if (loading)
    return (
      <Container>
        <Skeleton active paragraph={{ rows: 10 }} />
      </Container>
    );
  if (error)
    return (
      <Container>
        <p>Failed to load: {String(error.message || error)}</p>
      </Container>
    );
  if (!property)
    return (
      <Container>
        <p>Property not found.</p>
      </Container>
    );

  return (
    <>
      <Container>
        <Space
          direction="vertical"
          size={24}
          style={{ width: "100%", paddingTop: 30, paddingBottom: 30 }}
        >
          <Header
            title={property.title}
            city={property.city}
            country={property.country}
          />

          <Gallery images={images} onShowAll={() => setShowAll(true)} />

          <Subline
            locationLabel={`${property.city || ""}${
              property.country
                ? (property.city ? ", " : "") + property.country
                : ""
            }`}
            guests={property.guests}
            bedrooms={property.bedrooms}
            beds={property.beds}
            bathrooms={property.bathrooms}
          />

          <BadgeRow
            leftBadgeTitle="Guest favorite"
            leftBadgeText="One of the most loved homes on Mogul, according to guests"
            rating={property.rating}
            reviewsCount={property.reviews}
          />

          <BodyGrid
            description={property.description}
            amenities={amenities}
            deposit={property.deposit}
            dateAvailable={property.dateAvailable}
            priceSummary={property.priceSummary}
          />
        </Space>
      </Container>

      <Modal
        title="All photos"
        open={showAll}
        onCancel={() => setShowAll(false)}
        footer={null}
        width={1000}
      >
        <div
          style={{
            display: "grid",
            gap: 8,
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          }}
        >
          {images.map((src, i) => (
            <div
              key={i}
              style={{
                borderRadius: 12,
                overflow: "hidden",
                background: "#f0f0f0",
              }}
            >
              <img
                src={src}
                alt={`img-${i}`}
                style={{
                  width: "100%",
                  height: 220,
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}
